import { MouseEventHandler, forwardRef, CSSProperties } from "react";

import { ButtonTheme } from "@ui/types/enums/ButtonTheme";
import { FontClass } from "@ui/types/enums/FontClass";
import { GlobalClass } from "@ui/types/enums/GlobalClass";
import Link from "next/link";
import { joinClasses } from "@ui/utils/joinClasses";
import styles from "@ui/components/buttons/css/ButtonWithText.module.css";
import { LoadingSpinner } from "@ui/components/loading/LoadingSpinner";
import { ColorValue } from "@ui/types/enums/ColorValue";
import { assertUnreachable } from "shared";
import { FlexBox } from "@ui/components/layout/FlexBox";
import { AnchorTarget } from "@ui/types/AnchorTarget";
import { ElementId } from "@ui/types/enums/ElementId";

type Props = {
  buttonTheme: ButtonTheme;
  children: string | JSX.Element | Array<string | JSX.Element>;
  className?: string;
  disabled?: boolean;
  fontClass: FontClass;
  height?: number;
  href?: string;
  icon?: JSX.Element;
  iconPosition?: "left" | "right";
  id?: ElementId;
  isLoading?: boolean;
  logEvent?: () => void;
  onClick?: MouseEventHandler;
  style?: CSSProperties;
  target?: AnchorTarget;
  textTransform?: "none" | "uppercase";
  type?: "button" | "link_external" | "link_internal" | "submit";
  width?: "auto" | "100%";
};

const BUTTON_THEME_TO_SPINNER_COLOR = {
  [ButtonTheme.Primary]: ColorValue.White,
} as const;

function getClassNameForButtonTheme(
  buttonTheme: ButtonTheme,
  isLink: boolean,
  disabled: boolean
): string {
  switch (buttonTheme) {
    case ButtonTheme.Primary:
      return joinClasses(
        isLink ? styles.primaryThemeLink : styles.primaryTheme,
        disabled ? styles.disabled : null
      );
    default:
      return assertUnreachable(buttonTheme);
  }
}

// forwardRef required to make this work with NextJS Link, see
// https://nextjs.org/docs/api-reference/next/link#if-the-child-is-a-function-component.
export const ButtonWithText = forwardRef<HTMLButtonElement, Props>(
  function ButtonWithTextInner(
    {
      buttonTheme,
      children,
      className,
      disabled = false,
      fontClass,
      height,
      href,
      icon,
      iconPosition = "right",
      id,
      onClick,
      isLoading = false,
      logEvent,
      style = {},
      target = "_blank",
      textTransform,
      type = "button",
      width = "auto",
    }: Props,
    ref
  ) {
    const styleToUse = {
      ...style,
      height,
      ...(textTransform == null ? {} : { textTransform }),
    };
    const classNameJoined = joinClasses(
      getClassNameForButtonTheme(
        buttonTheme,
        type === "link_internal" || type === "link_external",
        disabled
      ),
      styles.button,
      width === "auto" ? styles.buttonAutoWidth : undefined,
      fontClass,
      className
    );
    const childrenWithIcon = (
      <>
        {icon && iconPosition === "left" && (
          <div
            className={joinClasses(
              styles.icon,
              styles.iconLeft,
              GlobalClass.HideText
            )}
          >
            {icon}
          </div>
        )}
        <FlexBox flexDirection="column">
          {isLoading && (
            <LoadingSpinner
              colorValue={BUTTON_THEME_TO_SPINNER_COLOR[buttonTheme]}
              fontClass={fontClass}
            />
          )}
          <div
            // Do this to keep the width of the button the same even if LoadingSpinner is rendered
            className={joinClasses(isLoading ? styles.hideChildren : undefined)}
          >
            {children}
          </div>
        </FlexBox>
        {icon && iconPosition === "right" && (
          <div
            className={joinClasses(
              styles.icon,
              styles.iconRight,
              GlobalClass.HideText
            )}
          >
            {icon}
          </div>
        )}
      </>
    );
    const onClickWithLog: MouseEventHandler = (e) => {
      if (logEvent != null) {
        logEvent();
      }
      if (onClick != null) {
        onClick(e);
      }
    };

    if (type === "link_internal") {
      return (
        <Link className={styles.linkInternal} id={id} href={href ?? ""}>
          <div
            className={joinClasses(classNameJoined, styles.linkContent)}
            onClick={onClickWithLog}
            style={styleToUse}
          >
            {childrenWithIcon}
          </div>
        </Link>
      );
    }

    if (type === "link_external") {
      return (
        <a
          className={joinClasses(classNameJoined, styles.linkContent)}
          href={href ?? ""}
          id={id}
          onClick={onClickWithLog}
          style={styleToUse}
          target={target}
          rel={target === "_blank" ? "noreferrer" : undefined}
        >
          {childrenWithIcon}
        </a>
      );
    }

    return (
      <button
        ref={ref}
        className={classNameJoined}
        disabled={disabled || isLoading}
        id={id}
        onClick={onClickWithLog}
        style={styleToUse}
        // eslint-disable-next-line react/button-has-type
        type={type}
      >
        {childrenWithIcon}
      </button>
    );
  }
);
